import { Link } from "react-router-dom";
import { Sprout, Flame, Crown, Sparkles, Gift, Zap, Trophy, Users, CalendarCheck, Target, GraduationCap, Megaphone, Check, ArrowRight } from "lucide-react";

const TIERS = [
  {
    name: "Noobie", icon: Sprout, commission: "15%", requirement: "Entry level",
    accent: "text-emerald-600", ring: "border-slate-200",
    perks: ["Standard ad inventory", "Basic affiliate access"],
  },
  {
    name: "Rising", icon: Flame, commission: "17%", requirement: "500+ clicks/month",
    accent: "text-orange-500", ring: "border-slate-200",
    perks: ["Better ad inventory", "Mid-tier affiliates unlocked", "Priority support"],
  },
  {
    name: "Elite", icon: Crown, commission: "19%", requirement: "1,500+ clicks/month",
    accent: "text-amber-500", ring: "border-amber-300",
    perks: ["Premium ad inventory", "High-paying affiliates unlocked", "Dedicated support + exclusive training"],
  },
  {
    name: "Superstar", icon: Sparkles, commission: "21%", requirement: "3,000+ clicks/month",
    accent: "text-emerald-400", ring: "border-emerald-500", dark: true,
    perks: ["Exclusive top-brand affiliates", "Negotiated rates", "1-on-1 coaching + featured spotlight on site"],
  },
];

const BONUSES = [
  { icon: Gift, title: "Sign-up Bonus", value: "$25 ad credits", desc: "Awarded upon completing verification and signing your contract." },
  { icon: Zap, title: "Fast-Start Bonus", value: "$50", desc: "Generate 100+ clicks in your first 30 days and pocket a $50 bonus." },
  { icon: Trophy, title: "Top Performer", value: "$200 / month", desc: "Monthly cash prize for the #1 affiliate by click value." },
  { icon: Users, title: "Referral Bonus", value: "$15 each", desc: "Earn $15 for every new verified affiliate you refer." },
  { icon: CalendarCheck, title: "Loyalty Bonus", value: "$100 – $250", desc: "$100 at 6 months active, $250 at 12 months active." },
  { icon: Target, title: "Milestone Bonuses", value: "up to $200", desc: "$25 at your first $500 earned, $75 at $1,000, $200 at $5,000." },
  { icon: GraduationCap, title: "Exclusive Training", value: "Tier-gated", desc: "Rising+ unlocks social growth guides, Elite+ unlocks the ad strategy masterclass, Superstar gets 1-on-1 coaching." },
  { icon: Megaphone, title: "Featured Spotlight", value: "Monthly", desc: "Superstar affiliates are featured on the Social Flats website every month." },
];

export default function RewardsPage() {
  return (
    <main data-testid="rewards-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <p className="uppercase text-xs tracking-[0.2em] font-semibold text-emerald-600 mb-2">Affiliate Rewards</p>
      <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-4">Climb tiers. Stack bonuses. Get paid more.</h1>
      <p className="text-slate-600 max-w-2xl mb-14 leading-relaxed">
        Every click moves you up. Higher tiers unlock better affiliate access, bigger commissions and exclusive perks — and the bonuses stack on top.
      </p>

      {/* Tiers */}
      <section className="mb-24">
        <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-8">The 4 tiers</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TIERS.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.name} data-testid={`tier-card-${t.name.toLowerCase()}`}
                className={`card-lift rounded-xl p-7 border flex flex-col h-full ${t.dark ? "bg-slate-900 text-white" : "bg-white"} ${t.ring}`}>
                <Icon className={`w-8 h-8 mb-4 ${t.accent}`} strokeWidth={1.8} />
                <p className={`font-heading text-xl font-semibold ${t.dark ? "text-white" : ""}`}>{t.name}</p>
                <p className={`text-xs mb-5 ${t.dark ? "text-slate-400" : "text-slate-500"}`}>{t.requirement}</p>
                <p className={`font-heading text-4xl font-medium mb-1 ${t.dark ? "text-emerald-400" : "text-emerald-700"}`}>{t.commission}</p>
                <p className={`text-[10px] uppercase tracking-[0.2em] font-semibold mb-6 ${t.dark ? "text-slate-500" : "text-slate-400"}`}>commission</p>
                <ul className="space-y-2.5 mt-auto">
                  {t.perks.map((p) => (
                    <li key={p} className={`flex gap-2 text-xs leading-relaxed ${t.dark ? "text-slate-300" : "text-slate-600"}`}>
                      <Check className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${t.accent}`} /> {p}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* Bonuses */}
      <section className="mb-20">
        <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-3">Bonuses that stack on top</h2>
        <p className="text-slate-600 max-w-xl mb-10 leading-relaxed">Cash rewards and perks paid in addition to your tier commission.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {BONUSES.map(({ icon: Icon, title, value, desc }) => (
            <div key={title} data-testid={`bonus-card-${title.toLowerCase().replace(/\s+/g, "-")}`} className="card-lift bg-white border border-slate-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2"><Icon className="w-5 h-5 text-emerald-600" /></div>
                <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">{value}</span>
              </div>
              <p className="font-heading font-semibold text-sm mb-1.5">{title}</p>
              <p className="text-xs text-slate-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-slate-900 rounded-2xl p-10 md:p-14 overflow-hidden text-center">
        <div className="noise-overlay" />
        <div className="relative">
          <h2 className="font-heading text-3xl md:text-4xl tracking-tight text-white mb-3">Start at Noobie. Retire at Superstar.</h2>
          <p className="text-slate-400 mb-8 max-w-lg mx-auto">Sign up free through Social Flats, grab your $25 ad credits, and start climbing today.</p>
          <Link to="/signup" data-testid="rewards-join-cta"
            className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-emerald-600 text-white font-medium hover:bg-emerald-500 transition-colors duration-200">
            Join Free Now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}
